// src/components/layout/views/FunctionalView.jsx

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { 
  FiMaximize, FiMinimize, FiZoomIn, FiZoomOut, FiDownload,
  FiChevronDown, FiChevronRight, FiSearch, FiX,
  FiCrosshair, FiMaximize2, FiEye, FiEyeOff,
  FiRefreshCw, FiArrowRight, FiArrowLeft, FiBox,
  FiLayers, FiCpu, FiDatabase, FiZap, FiShield,
  FiGlobe, FiActivity, FiPlay, FiPause
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

// ============================================================
// 3D LAYERED FUNCTIONAL FLOW VISUALIZATION
// ============================================================

const LAYER_CONFIG = {
  'layer1': { name: 'Physical Substrate', color: '#f97316', y: 0, icon: '🔧' },
  'layer2': { name: 'Environmental Interface', color: '#06b6d4', y: -2.5, icon: '📡' },
  'layer3': { name: 'Data Acquisition', color: '#10b981', y: -5, icon: '📥' },
  'layer4': { name: 'Data Storage', color: '#8b5cf6', y: -7.5, icon: '💾' },
  'layer5': { name: 'Data Processing', color: '#ec4899', y: -10, icon: '🔄' },
  'layer6': { name: 'Model & Intelligence', color: '#3b82f6', y: -12.5, icon: '🧠' },
  'layer7': { name: 'Optimization', color: '#f59e0b', y: -15, icon: '⚙️' },
  'layer8': { name: 'Execution Engine', color: '#ef4444', y: -17.5, icon: '⚡' },
  'layer9': { name: 'Orchestration', color: '#14b8a6', y: -20, icon: '🎯' },
  'layer10': { name: 'Application', color: '#6366f1', y: -22.5, icon: '💻' },
  'layer11': { name: 'Observability', color: '#0ea5e9', y: -25, icon: '📊' },
  'layer12': { name: 'Governance', color: '#64748b', y: -27.5, icon: '⚖️' }
};

const FunctionalView = () => {
  const { state, dispatch } = useSolution();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const mainContainerRef = useRef(null);
  
  // 3D State
  const [rotationX, setRotationX] = useState(-30); // Tilt angle
  const [rotationY, setRotationY] = useState(0); // Rotation around Y
  const [rotationZ, setRotationZ] = useState(0); // Rotation around Z
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragType, setDragType] = useState(null); // 'rotate' | 'pan' | 'zoom'
  
  const [expandedLayers, setExpandedLayers] = useState(new Set(Object.keys(LAYER_CONFIG)));
  const [expandedSubLayers, setExpandedSubLayers] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [showFlowLines, setShowFlowLines] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showAllComponents, setShowAllComponents] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  const animationRef = useRef(null);

  // ==================== DIMENSIONS ====================
  
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => { window.removeEventListener('resize', updateDimensions); observer.disconnect(); };
  }, []);

  // ==================== ORGANIZE COMPONENTS BY LAYER ====================
  
  const layeredComponents = useMemo(() => {
    const layers = {};
    
    Object.keys(LAYER_CONFIG).forEach(layerId => {
      layers[layerId] = {
        ...LAYER_CONFIG[layerId],
        layerId,
        subLayers: {}
      };
    });
    
    state.selectedComponents.forEach(comp => {
      const layerId = comp.layerId || 'unknown';
      const subLayerId = comp.subLayerId || 'unknown';
      
      if (!layers[layerId]) {
        layers[layerId] = {
          name: comp.layerName || `Layer ${layerId}`,
          color: '#94a3b8',
          y: -Object.keys(layers).length * 2.5,
          icon: '📦',
          layerId,
          subLayers: {}
        };
      }
      
      if (!layers[layerId].subLayers[subLayerId]) {
        layers[layerId].subLayers[subLayerId] = {
          subLayerId,
          name: comp.subLayerName || 'Sub-Layer',
          components: []
        };
      }
      
      layers[layerId].subLayers[subLayerId].components.push(comp);
    });
    
    return layers;
  }, [state.selectedComponents]);

  // ==================== 3D PROJECTION ====================
  
  const project3D = useCallback((x, y, z) => {
    // Apply rotations
    const cosX = Math.cos(rotationX * Math.PI / 180);
    const sinX = Math.sin(rotationX * Math.PI / 180);
    const cosY = Math.cos(rotationY * Math.PI / 180);
    const sinY = Math.sin(rotationY * Math.PI / 180);
    const cosZ = Math.cos(rotationZ * Math.PI / 180);
    const sinZ = Math.sin(rotationZ * Math.PI / 180);
    
    // Rotate around X
    let y1 = y * cosX - z * sinX;
    let z1 = y * sinX + z * cosX;
    
    // Rotate around Y
    let x2 = x * cosY + z1 * sinY;
    let z2 = -x * sinY + z1 * cosY;
    
    // Rotate around Z
    let x3 = x2 * cosZ - y1 * sinZ;
    let y3 = x2 * sinZ + y1 * cosZ;
    
    // Apply zoom and pan
    const scale = zoom * 40;
    return {
      x: x3 * scale + dimensions.width / 2 + panX,
      y: y3 * scale + dimensions.height / 2 + panY,
      z: z2,
      scale
    };
  }, [rotationX, rotationY, rotationZ, zoom, panX, panY, dimensions]);

  // ==================== COMPUTE LAYOUT ====================
  
  const layout = useMemo(() => {
    const layerEntries = Object.entries(layeredComponents).filter(([id]) => expandedLayers.has(id));
    
    return layerEntries.map(([layerId, layer], layerIndex) => {
      const totalLayers = layerEntries.length;
      const layerX = 0; // Center X
      const layerY = layerIndex * 2.5 - (totalLayers * 1.25); // Stack vertically
      const layerZ = 0;
      
      // Compute sub-layers
      const subLayerEntries = Object.entries(layer.subLayers);
      const subLayers = subLayerEntries.map(([subLayerId, subLayer], subIndex) => {
        const totalSubLayers = subLayerEntries.length;
        const angleStep = (2 * Math.PI) / Math.max(totalSubLayers, 1);
        const angle = subIndex * angleStep;
        const radius = 2;
        
        const subLayerX = Math.cos(angle) * radius;
        const subLayerZ = Math.sin(angle) * radius;
        
        // Compute components in this sub-layer
        const components = subLayer.components.map((comp, compIndex) => {
          const totalComps = subLayer.components.length;
          const compAngleStep = (2 * Math.PI) / Math.max(totalComps, 1);
          const compAngle = compIndex * compAngleStep;
          const compRadius = 1.5;
          
          return {
            ...comp,
            x: subLayerX + Math.cos(compAngle) * compRadius,
            z: subLayerZ + Math.sin(compAngle) * compRadius,
            y: 0
          };
        });
        
        return {
          ...subLayer,
          x: subLayerX,
          z: subLayerZ,
          components
        };
      });
      
      return {
        ...layer,
        x: layerX,
        y: layerY,
        z: layerZ,
        subLayers
      };
    });
  }, [layeredComponents, expandedLayers]);

  // ==================== COMPUTE FLOW CONNECTIONS ====================
  
  const flowConnections = useMemo(() => {
    const connections = [];
    
    // Connect layers in sequence
    const sortedLayers = layout.sort((a, b) => a.y - b.y);
    
    for (let i = 0; i < sortedLayers.length - 1; i++) {
      const currentLayer = sortedLayers[i];
      const nextLayer = sortedLayers[i + 1];
      
      // Connect components from current layer to next layer
      const currentComps = currentLayer.subLayers.flatMap(sl => sl.components);
      const nextComps = nextLayer.subLayers.flatMap(sl => sl.components);
      
      if (currentComps.length > 0 && nextComps.length > 0) {
        // Connect nearest components
        const connectionsToMake = Math.min(currentComps.length, nextComps.length);
        
        for (let j = 0; j < connectionsToMake; j++) {
          connections.push({
            from: {
              ...currentComps[j],
              layerY: currentLayer.y,
              layerColor: currentLayer.color
            },
            to: {
              ...nextComps[j],
              layerY: nextLayer.y,
              layerColor: nextLayer.color
            },
            type: 'data-flow',
            label: 'Data Flow'
          });
        }
      }
    }
    
    return connections;
  }, [layout]);

  // ==================== MOUSE HANDLERS ====================
  
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    
    // Determine drag type based on button/modifier
    if (e.button === 0 && e.shiftKey) {
      setDragType('pan');
    } else if (e.button === 0 && e.ctrlKey) {
      setDragType('zoom');
    } else if (e.button === 0) {
      setDragType('rotate');
    } else if (e.button === 2) {
      setDragType('pan');
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !dragType) return;
    
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    
    switch (dragType) {
      case 'rotate':
        setRotationY(prev => prev + dx * 0.3);
        setRotationX(prev => Math.max(-80, Math.min(0, prev + dy * 0.3)));
        break;
      case 'pan':
        setPanX(prev => prev + dx);
        setPanY(prev => prev + dy);
        break;
      case 'zoom':
        setZoom(prev => Math.max(0.2, Math.min(5, prev + dy * 0.01)));
        break;
      default:
        break;
    }
    
    setDragStart({ x: e.clientX, y: e.clientY });
  }, [isDragging, dragType, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(prev => Math.max(0.2, Math.min(5, prev - e.deltaY * 0.001)));
  }, []);

  // ==================== RESET VIEW ====================
  
  const resetView = useCallback(() => {
    setRotationX(-30);
    setRotationY(0);
    setRotationZ(0);
    setZoom(1);
    setPanX(0);
    setPanY(0);
    setExpandedLayers(new Set(Object.keys(LAYER_CONFIG)));
    setExpandedSubLayers(new Set());
    setSelectedNode(null);
  }, []);

  // ==================== FULLSCREEN ====================
  
  const toggleFullscreen = useCallback(() => {
    const element = mainContainerRef.current;
    if (!element) return;
    if (!document.fullscreenElement) {
      element.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F11') { e.preventDefault(); toggleFullscreen(); }
      if (e.key === 'r' && e.ctrlKey) { e.preventDefault(); resetView(); }
      if (e.key === 'Escape') setSelectedNode(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen, resetView]);

  // ==================== AUTO-ROTATION ====================
  
  const toggleAutoRotate = useCallback(() => {
    setIsAnimating(prev => !prev);
  }, []);

  useEffect(() => {
    if (isAnimating) {
      animationRef.current = setInterval(() => {
        setRotationY(prev => (prev + 0.5) % 360);
      }, 50);
    } else {
      if (animationRef.current) clearInterval(animationRef.current);
    }
    return () => {
      if (animationRef.current) clearInterval(animationRef.current);
    };
  }, [isAnimating]);

  // ==================== RENDER ====================

  return (
    <div 
      ref={mainContainerRef}
      className={`h-full flex flex-col ${isFullscreen ? 'bg-black' : 'bg-gradient-to-br from-gray-900 to-gray-800'}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FiLayers className="w-5 h-5 text-blue-400" />
              3D Functional Flow Visualization
            </h2>
            <p className="text-xs text-gray-400">
              {state.selectedComponents.length} components across {Object.keys(layeredComponents).length} layers
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Toggle Flow Lines */}
            <button
              onClick={() => setShowFlowLines(!showFlowLines)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${showFlowLines ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              {showFlowLines ? 'Flow: On' : 'Flow: Off'}
            </button>
            
            {/* Toggle Labels */}
            <button
              onClick={() => setShowLabels(!showLabels)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${showLabels ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              {showLabels ? 'Labels: On' : 'Labels: Off'}
            </button>
            
            {/* Auto-Rotate */}
            <button
              onClick={toggleAutoRotate}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${isAnimating ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              {isAnimating ? <FiPause className="w-3 h-3" /> : <FiPlay className="w-3 h-3" />}
              {isAnimating ? 'Stop' : 'Rotate'}
            </button>
            
            {/* Zoom Controls */}
            <button onClick={() => setZoom(prev => Math.min(5, prev * 1.2))} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600">
              <FiZoomIn className="w-4 h-4 text-white" />
            </button>
            <button onClick={() => setZoom(prev => Math.max(0.2, prev / 1.2))} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600">
              <FiZoomOut className="w-4 h-4 text-white" />
            </button>
            
            {/* Reset */}
            <button onClick={resetView} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600" title="Reset View (Ctrl+R)">
              <FiCrosshair className="w-4 h-4 text-white" />
            </button>
            
            {/* Fullscreen */}
            <button onClick={toggleFullscreen} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600">
              {isFullscreen ? <FiMinimize className="w-4 h-4 text-white" /> : <FiMaximize2 className="w-4 h-4 text-white" />}
            </button>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="flex items-center gap-4 text-xs text-gray-400 mt-2 flex-wrap">
          <span>🖱️ Drag: Rotate</span>
          <span>⇧ Shift+Drag: Pan</span>
          <span>⌃ Ctrl+Drag: Zoom</span>
          <span>Scroll: Zoom</span>
          <span>Right-click+Drag: Pan</span>
          <span>Ctrl+R: Reset</span>
          <span>F11: Fullscreen</span>
        </div>
      </div>
      
      {/* 3D Canvas */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden" style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          className="w-full h-full"
          style={{ background: 'radial-gradient(circle, #1a1a2e, #0a0a0a)' }}
        >
          <defs>
            {/* Glow Filter */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            
            {/* Shadow */}
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="#000" flood-opacity="0.5" />
            </filter>
            
            {/* Gradients */}
            {Object.entries(LAYER_CONFIG).map(([layerId, layer]) => (
              <linearGradient key={layerId} id={`layer-grad-${layerId}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={layer.color} stopOpacity="0.8" />
                <stop offset="100%" stopColor={layer.color} stopOpacity="0.3" />
              </linearGradient>
            ))}
          </defs>
          
          {/* ==================== FLOW CONNECTIONS ==================== */}
          {showFlowLines && flowConnections.map((conn, i) => {
            const from = project3D(conn.from.x, conn.from.layerY + conn.from.y, conn.from.z);
            const to = project3D(conn.to.x, conn.to.layerY + conn.to.y, conn.to.z);
            
            const isHighlighted = selectedNode && (selectedNode.id === conn.from.id || selectedNode.id === conn.to.id);
            const isDimmed = selectedNode && !isHighlighted;
            
            return (
              <path
                key={i}
                d={`M${from.x},${from.y} C${from.x},${(from.y + to.y) / 2} ${to.x},${(from.y + to.y) / 2} ${to.x},${to.y}`}
                fill="none"
                stroke={isHighlighted ? '#ef4444' : conn.from.layerColor}
                strokeWidth={isHighlighted ? 3 : 1.5}
                strokeOpacity={isDimmed ? 0.05 : isHighlighted ? 0.9 : 0.4}
                strokeDasharray="6,4"
                style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
              />
            );
          })}
          
          {/* ==================== LAYER PLANES ==================== */}
          {layout.map((layer) => {
            const center = project3D(layer.x, layer.y, layer.z);
            const planeWidth = 6 * layer.scale / 40 * zoom * 40;
            const planeHeight = 1.5 * layer.scale / 40 * zoom * 40;
            
            const corners = [
              project3D(-3, layer.y, -1.5),
              project3D(3, layer.y, -1.5),
              project3D(3, layer.y, 1.5),
              project3D(-3, layer.y, 1.5)
            ];
            
            const isExpanded = expandedLayers.has(layer.layerId);
            
            return (
              <g key={layer.layerId}>
                {/* Layer Plane */}
                <polygon
                  points={corners.map(c => `${c.x},${c.y}`).join(' ')}
                  fill={`url(#layer-grad-${layer.layerId})`}
                  stroke={layer.color}
                  strokeWidth={selectedNode?.layerId === layer.layerId ? 3 : 1.5}
                  strokeOpacity={0.8}
                  filter="url(#shadow)"
                  opacity={isExpanded ? 0.7 : 0.3}
                  style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                  onClick={() => {
                    const newSet = new Set(expandedLayers);
                    if (newSet.has(layer.layerId)) newSet.delete(layer.layerId);
                    else newSet.add(layer.layerId);
                    setExpandedLayers(newSet);
                  }}
                />
                
                {/* Layer Label */}
                {showLabels && (
                  <g
                    transform={`translate(${center.x}, ${center.y - 25})`}
                    style={{ pointerEvents: 'none' }}
                  >
                    <text
                      textAnchor="middle"
                      fill={layer.color}
                      fontSize="14"
                      fontWeight="bold"
                      style={{ textShadow: '0 0 10px rgba(0,0,0,0.8)' }}
                    >
                      {layer.icon} {layer.name}
                    </text>
                    {isExpanded && (
                      <text
                        textAnchor="middle"
                        fill="#9ca3af"
                        fontSize="10"
                        y="15"
                      >
                        {Object.values(layer.subLayers).reduce((sum, sl) => sum + sl.components.length, 0)} components
                      </text>
                    )}
                  </g>
                )}
                
                {/* Expand/Collapse Indicator */}
                <g
                  transform={`translate(${center.x + 50}, ${center.y - 25})`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const newSet = new Set(expandedLayers);
                    if (newSet.has(layer.layerId)) newSet.delete(layer.layerId);
                    else newSet.add(layer.layerId);
                    setExpandedLayers(newSet);
                  }}
                >
                  <circle r="8" fill="rgba(0,0,0,0.5)" stroke={layer.color} strokeWidth="1.5" />
                  <text textAnchor="middle" dy="0.35em" fill="white" fontSize="10" fontWeight="bold">
                    {isExpanded ? '−' : '+'}
                  </text>
                </g>
                
                {/* ==================== SUB-LAYERS ==================== */}
                {isExpanded && layer.subLayers.map((subLayer) => {
                  const subCenter = project3D(
                    layer.x + subLayer.x,
                    layer.y,
                    layer.z + subLayer.z
                  );
                  
                  const isSubExpanded = expandedSubLayers.has(subLayer.subLayerId);
                  
                  return (
                    <g key={subLayer.subLayerId}>
                      {/* Sub-layer circle */}
                      <circle
                        cx={subCenter.x}
                        cy={subCenter.y}
                        r={isSubExpanded ? 20 : 15}
                        fill="rgba(0,0,0,0.4)"
                        stroke={layer.color}
                        strokeWidth="1"
                        strokeDasharray="3,3"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const newSet = new Set(expandedSubLayers);
                          if (newSet.has(subLayer.subLayerId)) newSet.delete(subLayer.subLayerId);
                          else newSet.add(subLayer.subLayerId);
                          setExpandedSubLayers(newSet);
                        }}
                      />
                      
                      {/* Sub-layer label */}
                      {showLabels && (
                        <text
                          x={subCenter.x}
                          y={subCenter.y - 20}
                          textAnchor="middle"
                          fill="#e5e7eb"
                          fontSize="8"
                        >
                          {subLayer.name}
                        </text>
                      )}
                      
                      {/* Components in sub-layer */}
                      {isSubExpanded && subLayer.components.map((comp, compIdx) => {
                        const compPos = project3D(
                          layer.x + subLayer.x + comp.x,
                          layer.y + comp.y,
                          layer.z + subLayer.z + comp.z
                        );
                        
                        const isSelected = selectedNode?.id === comp.id;
                        const isHovered = hoveredNode === comp.id;
                        
                        return (
                          <g
                            key={comp.id}
                            transform={`translate(${compPos.x}, ${compPos.y})`}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedNode(isSelected ? null : comp);
                            }}
                            onMouseEnter={() => setHoveredNode(comp.id)}
                            onMouseLeave={() => setHoveredNode(null)}
                          >
                            {/* Component node */}
                            <circle
                              r={isSelected ? 12 : isHovered ? 10 : 8}
                              fill={isSelected ? '#ef4444' : isHovered ? '#60a5fa' : '#374151'}
                              stroke={isSelected ? '#ef4444' : layer.color}
                              strokeWidth={isSelected ? 3 : 2}
                              filter={isSelected || isHovered ? 'url(#glow)' : 'none'}
                              style={{ transition: 'all 0.3s ease' }}
                            />
                            
                            {/* Component icon */}
                            <text
                              textAnchor="middle"
                              dy="0.35em"
                              fontSize="8"
                              style={{ pointerEvents: 'none' }}
                            >
                              {comp.icon || '🔧'}
                            </text>
                            
                            {/* Component label */}
                            {showLabels && (isHovered || isSelected) && (
                              <text
                                y="-15"
                                textAnchor="middle"
                                fill="white"
                                fontSize="8"
                                fontWeight="bold"
                                style={{ textShadow: '0 0 5px rgba(0,0,0,0.8)', pointerEvents: 'none' }}
                              >
                                {comp.name.length > 15 ? comp.name.substring(0, 13) + '...' : comp.name}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </g>
                  );
                })}
              </g>
            );
          })}
          
          {/* Selected Component Info Panel */}
          {selectedNode && (
            <g
              transform={`translate(20, 20)`}
              style={{ pointerEvents: 'none' }}
            >
              <rect
                width="250"
                height="100"
                rx="8"
                fill="rgba(0,0,0,0.8)"
                stroke="#3b82f6"
                strokeWidth="2"
              />
              <text x="15" y="25" fill="white" fontSize="12" fontWeight="bold">
                {selectedNode.icon} {selectedNode.name}
              </text>
              <text x="15" y="45" fill="#9ca3af" fontSize="9">
                Layer: {selectedNode.layerName || 'Unknown'}
              </text>
              <text x="15" y="60" fill="#9ca3af" fontSize="9">
                Sub-Layer: {selectedNode.subLayerName || 'Unknown'}
              </text>
              <text x="15" y="75" fill="#9ca3af" fontSize="9">
                Cost: ${selectedNode.cost?.low || 0} - ${selectedNode.cost?.high || 0}
              </text>
              <text x="15" y="90" fill="#ef4444" fontSize="8">
                Press ESC to close
              </text>
            </g>
          )}
        </svg>
        
        {/* Zoom Level Indicator */}
        <div className="absolute bottom-4 right-4 bg-gray-800 rounded-lg px-3 py-2 text-xs text-gray-400">
          Zoom: {zoom.toFixed(1)}x | Rotation: {Math.round(rotationY)}° | Tilt: {Math.abs(Math.round(rotationX))}°
        </div>
        
        {/* Layer Legend */}
        <div className="absolute bottom-4 left-4 bg-gray-800 rounded-lg p-3 max-h-48 overflow-y-auto">
          <div className="text-xs font-bold text-white mb-2">Layers ({Object.keys(layeredComponents).length})</div>
          {Object.entries(layeredComponents).map(([layerId, layer]) => (
            <div key={layerId} className="flex items-center gap-2 text-xs text-gray-300 mb-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: layer.color }}></span>
              <span>{layer.icon} {layer.name}</span>
              <span className="text-gray-500">
                ({Object.values(layer.subLayers).reduce((sum, sl) => sum + sl.components.length, 0)})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FunctionalView;